import {useEffect, useMemo, useState} from 'react';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import {TextField} from '~components/atoms/TextField';
import SearchIcon from '~assets/images/icons/ui/search.svg';
import {FiltersType} from '~providers/FiltersProvider';
import useFetchPlotsSuggestions from '~api/useFetchPlotsSuggestions';
import {Loader} from '~components/atoms/Loader';
import {Label} from '~components/atoms/Text';
import useFilterPartner from '~molecules/PlotSearch/useFilterPartner';
import usePlotClick from '~molecules/PlotSearch/usePlotClick';
import {PartnersResponse} from '~types/Partner';
import {closeSearchResults, Results} from '~molecules/PlotSearch/Results';

import {
    LoaderContainer,
    ResultsContainer,
    ResultsContainerPlaceholder,
    SearchbarContainer,
} from './styles';

type PlotSearchProps = {
    handleSearch: (searchTerms: string) => void;
    searchTerms: string;
    showPartnerLands: (partner: FiltersType['partner']) => void;
    partners?: PartnersResponse;
    isList?: boolean;
};

export const PlotSearch = ({
    handleSearch,
    searchTerms,
    showPartnerLands,
    partners,
    isList = false,
}: PlotSearchProps) => {
    const plotClick = usePlotClick();
    const [localSearchTerms, setLocalSearchTerms] = useState(searchTerms);
    const filteredPartners = useFilterPartner(partners, localSearchTerms);
    const {data: plots, isLoading} = useFetchPlotsSuggestions(localSearchTerms);
    const t = useScopeTranslation('common', 'filters', 'search');

    const data = useMemo(() => {
        return {
            plots: (plots || []).map(({id, address}) => ({id, text: address})),
            partners: filteredPartners.map((partner, i) => ({
                id: i,
                text: partner.name,
            })),
        };
    }, [plots, filteredPartners]);

    useEffect(() => {
        setLocalSearchTerms(searchTerms);
    }, [searchTerms]);

    useEffect(() => {
        if (!localSearchTerms) {
            if (searchTerms !== localSearchTerms) {
                handleSearch('');
            }
            closeSearchResults();
        }
    }, [localSearchTerms]);

    return (
        <SearchbarContainer>
            <TextField
                value={localSearchTerms}
                onChange={setLocalSearchTerms}
                icon={<SearchIcon />}
                placeholder={
                    Array.isArray(partners)
                        ? t('placeholderWithPartner')
                        : t('placeholder')
                }
                withClearBtn
                data-cy={`${isList ? 'list-' : 'map-'}plot-search-input`}
            />
            <ResultsContainerPlaceholder>
                <ResultsContainer
                    onClick={closeSearchResults}
                    data-cy={`${isList ? 'list-' : 'map-'}plot-search-results`}
                >
                    {localSearchTerms.length < 3 ? (
                        <Label>{t('minCharacters')}</Label>
                    ) : isLoading ? (
                        <LoaderContainer>
                            <Loader size={30} />
                        </LoaderContainer>
                    ) : data.plots.length || data.partners.length ? (
                        <>
                            <Results
                                sectionName={'partner'}
                                data={data.partners}
                                onClick={({text}) => showPartnerLands(text)}
                                handleSearch={handleSearch}
                            />
                            <Results
                                sectionName={'plot'}
                                data={data.plots}
                                onClick={({id}) => plotClick(id)}
                                handleSearch={handleSearch}
                            />
                        </>
                    ) : (
                        <Label>{t('notFound')}</Label>
                    )}
                </ResultsContainer>
            </ResultsContainerPlaceholder>
        </SearchbarContainer>
    );
};
